import React, { useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import { Box, Stack, Typography, IconButton, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { LineChart } from '@mui/x-charts/LineChart';
import { useConfig } from '../../components/ConfigContext';
import { useV4Resolve } from '../theme';

const cyan = 'var(--v4-cyan)';

const cardSx = {
  background: 'rgba(var(--v4-surface-rgb),0.88)',
  border: '1px solid rgba(var(--v4-cyan-rgb),0.12)',
  borderRadius: '18px',
  backdropFilter: 'blur(20px)',
  p: 3,
};

const labelSx = {
  fontSize: '0.58rem',
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.15em',
  color: 'rgba(var(--v4-cyan-rgb),0.5)',
  mb: 0.75,
  display: 'block',
};

const stepBtnSx = {
  width: 32,
  height: 32,
  border: '1px solid rgba(var(--v4-cyan-rgb),0.28)',
  borderRadius: '50%',
  color: cyan,
  background: 'rgba(var(--v4-cyan-rgb),0.05)',
  p: 0,
  '&:hover': { background: 'rgba(var(--v4-cyan-rgb),0.14)', borderColor: cyan },
  '&:disabled': { opacity: 0.3 },
};

const numInputStyle = {
  width: '5rem',
  textAlign: 'center',
  background: 'rgba(var(--v4-cyan-rgb),0.04)',
  border: '1px solid rgba(var(--v4-cyan-rgb),0.14)',
  borderRadius: 8,
  color: 'rgba(var(--v4-text-rgb),0.92)',
  fontSize: '1rem',
  fontWeight: 700,
  fontFamily: 'Inter, Roboto, sans-serif',
  padding: '7px 10px',
  outline: 'none',
};

const timePickerSx = {
  '& .MuiInputBase-root': {
    background: 'rgba(var(--v4-cyan-rgb),0.04)',
    borderRadius: '12px',
    color: 'rgba(var(--v4-text-rgb),0.92)',
    fontWeight: 600,
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--v4-cyan-rgb),0.18)' },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(var(--v4-cyan-rgb),0.38)' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: cyan },
  '& .MuiSvgIcon-root': { color: 'rgba(var(--v4-cyan-rgb),0.55)' },
  '& .MuiInputAdornment-root button': { color: 'rgba(var(--v4-cyan-rgb),0.55)' },
  '& input': { color: 'rgba(var(--v4-text-rgb),0.92)', fontWeight: 600 },
};

function V4Stepper({ value, onChange, min = 0, max = 100, step = 1 }) {
  const dec = () => onChange(Math.max(min, (value ?? 0) - step));
  const inc = () => onChange(Math.min(max, (value ?? 0) + step));
  const handleChange = e => {
    const v = parseFloat(e.target.value);
    if (!isNaN(v)) onChange(Math.max(min, Math.min(max, v)));
  };
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <IconButton onClick={dec} sx={stepBtnSx} size="small"><RemoveIcon sx={{ fontSize: 16 }} /></IconButton>
      <input value={value ?? ''} onChange={handleChange} style={numInputStyle} />
      <IconButton onClick={inc} sx={stepBtnSx} size="small"><AddIcon sx={{ fontSize: 16 }} /></IconButton>
    </Stack>
  );
}

function Field({ label, children }) {
  return (
    <Box>
      <Typography component="span" sx={labelSx}>{label}</Typography>
      {children}
    </Box>
  );
}

export default function V4ConfigLicht({ is_managed }) {
  const resolve = useV4Resolve();
  const { state, dispatch, saveConfig } = useConfig();
  const [dauer, setDauer] = useState(state.config.dauer ?? 30);

  const changeDauer = v => {
    setDauer(v);
    dispatch({ type: 'SET_FIELD', field: 'dauer', value: v });
    const sunrise = state.config.sun_rise_dayjs
      ? dayjs(state.config.sun_rise_dayjs)
      : dayjs('01/01/2024 ' + state.config.sun_rise);
    const sunset = state.config.sun_set_dayjs
      ? dayjs(state.config.sun_set_dayjs)
      : dayjs('01/01/2024 ' + state.config.sun_set_end);
    dispatch({ type: 'SET_FIELD', field: 'sun_rise_end', value: sunrise.add(v, 'minute').format('HH:mm') });
    dispatch({ type: 'SET_FIELD', field: 'sun_set', value: sunset.subtract(v, 'minute').format('HH:mm') });
  };

  const begin = dayjs('01/01/2024 00:00');
  const end   = dayjs('01/02/2024 00:00');

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
      <Box sx={cardSx}>
        <Stack direction="row" flexWrap="wrap" gap={3} useFlexGap>
          <Field label="Sonnenaufgang">
            <TimePicker
              ampm={false}
              views={['hours', 'minutes']}
              format="HH:mm"
              value={dayjs().hour(state.config.sun_rise.split(':')[0]).minute(state.config.sun_rise.split(':')[1])}
              onChange={e => {
                const x = dayjs(e);
                dispatch({ type: 'SET_FIELD', field: 'sun_rise', value: x.format('HH:mm') });
                dispatch({ type: 'SET_FIELD', field: 'sun_rise_dayjs', value: x });
                dispatch({ type: 'SET_FIELD', field: 'sun_rise_end', value: x.add(dauer, 'minute').format('HH:mm') });
              }}
              sx={timePickerSx}
              slotProps={{ textField: { size: 'small', label: '' } }}
            />
          </Field>

          <Field label="Sonnenuntergang">
            <TimePicker
              ampm={false}
              views={['hours', 'minutes']}
              format="HH:mm"
              value={dayjs('01/01/2024 ' + state.config.sun_set_end)}
              onChange={e => {
                const y = dayjs(e);
                dispatch({ type: 'SET_FIELD', field: 'sun_set_end', value: y.format('HH:mm') });
                dispatch({ type: 'SET_FIELD', field: 'sun_set_dayjs', value: y });
                dispatch({ type: 'SET_FIELD', field: 'sun_set', value: y.subtract(dauer, 'minute').format('HH:mm') });
              }}
              sx={timePickerSx}
              slotProps={{ textField: { size: 'small', label: '' } }}
            />
          </Field>

          <Field label="Dämmerung (Min.)">
            <V4Stepper value={state.config.dauer} onChange={changeDauer} min={0} max={120} step={10} />
          </Field>

          <Field label="Max. Intensität (%)">
            <V4Stepper
              value={state.config.max_intensity_light}
              onChange={v => dispatch({ type: 'SET_FIELD', field: 'max_intensity_light', value: v })}
              min={0} max={100} step={10}
            />
          </Field>
        </Stack>

        <Box sx={{ mt: 3, borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(var(--v4-cyan-rgb),0.08)', background: 'rgba(var(--v4-shadow-rgb),calc(0.2*var(--v4-shadow-k)))' }}>
          <LineChart
            xAxis={[{
              data: [
                begin,
                dayjs('01/01/2024 ' + state.config.sun_rise),
                dayjs('01/01/2024 ' + state.config.sun_rise_end),
                dayjs('01/01/2024 ' + state.config.sun_set),
                dayjs('01/01/2024 ' + state.config.sun_set_end),
                end,
              ],
              scaleType: 'time',
            }]}
            yAxis={[{ max: 100 }]}
            series={[{
              data: [0, 0, state.config.max_intensity_light, state.config.max_intensity_light, 0, 0],
              color: resolve(cyan),
              area: true,
            }]}
            height={180}
            sx={{
              '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': { stroke: 'rgba(var(--v4-cyan-rgb),0.18)' },
              '& .MuiChartsAxis-tickLabel': { fill: 'rgba(var(--v4-muted-rgb),0.45)', fontSize: '0.68rem' },
              '& .MuiAreaElement-root': { fill: 'rgba(var(--v4-cyan-rgb),0.09)' },
              '& .MuiLineElement-root': { strokeWidth: 2 },
              background: 'transparent',
            }}
          />
        </Box>

        {!is_managed && (
          <Button
            fullWidth variant="contained" startIcon={<SaveRoundedIcon />}
            onClick={saveConfig}
            sx={{
              mt: 2.5, borderRadius: '12px', fontWeight: 700, py: 1.2,
              background: 'linear-gradient(135deg,var(--v4-cyan),var(--v4-cyan-deep))',
              boxShadow: '0 4px 18px rgba(var(--v4-cyan-rgb),0.28)',
              '&:hover': { boxShadow: '0 6px 24px rgba(var(--v4-cyan-rgb),0.4)' },
            }}
          >
            Speichern
          </Button>
        )}
      </Box>
    </LocalizationProvider>
  );
}
